import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Check, ExternalLink, Loader2, Eye, Send, MousePointerClick, FileCheck2, PowerOff, BookOpen } from "lucide-react";

/**
 * ApplyLinkDialog — an introducer's own application link, from the desk's side of the phone.
 * Read it out, copy it, or hand them a QR to print, without asking them to log in and find it.
 */
export function ApplyLinkDialog({ party, onOpenChange }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedFunding, setCopiedFunding] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setData(null);
    if (!party) return;
    api.get(`/hub/referrers/${party.id}/apply-link`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load that link"); onOpenChange(false); });
  }, [party]); // eslint-disable-line

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Link copied — paste it straight to them");
    } catch { toast.error("Copy failed"); }
  };

  const copyFunding = async () => {
    try {
      await navigator.clipboard.writeText(data.funding_url);
      setCopiedFunding(true);
      setTimeout(() => setCopiedFunding(false), 1600);
      toast.success("Explainer link copied");
    } catch { toast.error("Copy failed"); }
  };

  const downloadQr = () => {
    const canvas = document.querySelector('[data-testid="hub-apply-qr"] canvas');
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `connexis-apply-${data.slug}.png`;
    a.click();
    toast.success("QR saved — send it over or print it");
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.patch(`/hub/referrers/${party.id}/apply-link`, { disabled: false });
      setData((d) => ({ ...d, disabled: false }));
      toast.success("Their link is live again");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not switch it back on"); }
    finally { setBusy(false); }
  };

  const s = data?.stats || {};

  return (
    <Dialog open={!!party} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="hub-apply-link-dialog">
        <DialogHeader>
          <DialogTitle>{party?.company || party?.name}'s application link</DialogTitle>
          <DialogDescription>
            Anyone who applies through this link is credited to them automatically. Safe to read down the phone.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="grid place-items-center py-14 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {data.disabled && (
              <div className="rounded-xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-3.5 flex items-start gap-3" data-testid="hub-apply-link-off">
                <PowerOff className="size-4 text-[#3B63A8] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#24457F]">They've switched this link off</div>
                  <p className="mt-0.5 text-[12px] text-[#2F5390]/80 leading-relaxed">
                    Anyone opening it gets a polite "ask your introducer for a new one". Switch it back on for them if that was a mistake.
                  </p>
                  <Button onClick={reopen} disabled={busy} size="sm" variant="outline"
                          className="mt-2 h-7 rounded-[10px] text-[11.5px] bg-white" data-testid="hub-apply-link-reopen">
                    {busy ? <Loader2 className="size-3 animate-spin" /> : "Switch it back on"}
                  </Button>
                </div>
              </div>
            )}
            {!data.applications_open && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[12px] text-slate-600" data-testid="hub-apply-links-closed">
                Customer application links are switched off platform-wide under Automation — every partner's link is
                politely closed until you turn that back on.
              </div>
            )}

            <div className="flex items-stretch gap-2 flex-wrap">
              <div className="flex-1 min-w-[220px] rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 font-mono text-[12.5px] text-slate-700 truncate"
                   data-testid="hub-apply-link-url">
                {data.url.replace(/^https?:\/\//, "")}
              </div>
              <Button onClick={copy} variant="outline" className="h-[46px] rounded-xl" data-testid="hub-apply-link-copy">
                {copied ? <><Check className="size-4 mr-1.5 text-teal-500" /> Copied</> : <><Copy className="size-4 mr-1.5" /> Copy</>}
              </Button>
              <a href={data.url} target="_blank" rel="noreferrer"
                 className="h-[46px] px-3.5 inline-flex items-center rounded-xl border border-slate-200 text-slate-600 hover:border-slate-300"
                 title="Open their form" data-testid="hub-apply-link-preview">
                <ExternalLink className="size-4" />
              </a>
            </div>

            <div className="rounded-xl border border-slate-200 p-4 flex items-center gap-4 flex-wrap" data-testid="hub-apply-qr">
              <QRCodeCanvas value={data.url} size={124} level="M" includeMargin bgColor="#ffffff" fgColor="#0B1220" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-900">A QR they can print</div>
                <p className="mt-0.5 text-[12px] text-slate-500 leading-relaxed max-w-xs">
                  Business cards, van livery, the back of an invoice. Scanning it opens their application form.
                </p>
                <Button onClick={downloadQr} size="sm" variant="outline" className="mt-2 h-8 rounded-[10px] text-[11.5px]"
                        data-testid="hub-apply-qr-download">
                  Download PNG
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4" data-testid="hub-funding-link">
              <div className="text-[13px] font-semibold text-slate-900">Their explainer link</div>
              <p className="mt-0.5 text-[12px] text-slate-500 leading-relaxed">
                The business-funding page, branded "sent to you by {party?.company || party?.name}". Every button on
                it leads to the form above, so a cold customer can read first and apply second.
              </p>
              <div className="mt-2.5 flex items-stretch gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px] rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 font-mono text-[12px] text-slate-700 truncate"
                     data-testid="hub-funding-link-url">
                  {(data.funding_url || "").replace(/^https?:\/\//, "")}
                </div>
                <Button onClick={copyFunding} variant="outline" className="h-[46px] rounded-xl" data-testid="hub-funding-link-copy">
                  {copiedFunding ? <><Check className="size-4 mr-1.5 text-teal-500" /> Copied</> : <><Copy className="size-4 mr-1.5" /> Copy</>}
                </Button>
                <a href={data.funding_url} target="_blank" rel="noreferrer"
                   className="h-[46px] px-3.5 inline-flex items-center rounded-xl border border-slate-200 text-slate-600 hover:border-slate-300"
                   title="Open their explainer page" data-testid="hub-funding-link-preview">
                  <ExternalLink className="size-4" />
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="hub-apply-link-stats">
              <Stat Icon={BookOpen} label="Explainer" value={s.funding_views} />
              <Stat Icon={Eye} label="Form views" value={s.views} />
              <Stat Icon={Send} label="Invites" value={s.invites_sent} />
              <Stat Icon={MousePointerClick} label="Started" value={s.started} />
              <Stat Icon={FileCheck2} label="Submitted" value={s.submitted} tone />
            </div>

            {!data.activated && (
              <div className="text-[12px] text-slate-500 leading-relaxed">
                They haven't set a password yet, so they can't see this link in their own portal. Send them the set-up
                link from the Invite column first, or just read this one out.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ Icon, label, value, tone }) {
  return (
    <div className={`min-w-0 rounded-xl border px-2.5 py-2 ${tone ? "border-teal-200 bg-teal-50/60" : "border-slate-200 bg-slate-50/60"}`}>
      <div className="truncate text-[9px] uppercase tracking-[0.06em] text-slate-400" title={label}>{label}</div>
      <div className="mt-1 flex items-center gap-1.5">
        <Icon className={`size-3 shrink-0 ${tone ? "text-teal-600/70" : "text-slate-400"}`} />
        <span className={`text-[18px] font-semibold leading-none tabular-nums ${tone ? "text-teal-700" : "text-slate-900"}`}>{value ?? 0}</span>
      </div>
    </div>
  );
}
